import { gql, useQuery } from "@apollo/client";
import { Grid } from "@mui/material";
import { ErrorComponent } from "@src/components";
import DetailLink from "@src/components/DetailLink";
import { DetailPageHeader } from "@src/components/headers/DetailPageHeader";
import LoadingComponent from "@src/components/LoadingComponent";
import EditForm from "@src/domain/devices/edit/Form";
// eslint-disable-next-line @comet/no-private-sibling-import
import { cameraFragment } from "@src/domain/devices/edit/fragments.gql";
import PurchaseInformation from "@src/domain/devices/edit/PurchaseInformation";
import SubscriptionInformation from "@src/domain/devices/edit/SubscriptionInformation";
import { type GQLCameraAdminQuery, type GQLCameraAdminQueryVariables } from "@src/graphql.generated";
import { type FC, type PropsWithChildren } from "react";
import { FormattedMessage } from "react-intl";

import ActivityInformation from "./activityInformation/ActivityInformation";
import ActivityLogTable from "./activityLog/ActivityLogTable";
import { DetailLinkIcon } from "./Camera.sc";

export const cameraQuery = gql`
    query CameraAdmin($id: ID!) {
        cameraAdmin(id: $id) {
            ...CameraFragment
        }
    }
    ${cameraFragment}
`;

interface Props {
    id: string;
}

const ShowUserLink: FC<PropsWithChildren<Props>> = ({ id }) => (
    <DetailLink to={`/users/${id}/edit`}>
        <DetailLinkIcon />
        <FormattedMessage id="reviermanager.camera.detail.user.link" defaultMessage="Benutzer zeigen" />
    </DetailLink>
);

export const EditCamera: FC<PropsWithChildren<Props>> = ({ id }) => {
    const { data, error, loading, refetch } = useQuery<GQLCameraAdminQuery, GQLCameraAdminQueryVariables>(cameraQuery, {
        variables: { id },
    });

    if (error) {
        return <ErrorComponent />;
    }

    if (loading || !data) {
        return <LoadingComponent />;
    }

    return (
        <>
            <DetailPageHeader>{data.cameraAdmin.user && <ShowUserLink id={data.cameraAdmin.user.id} />}</DetailPageHeader>
            <Grid container spacing={4}>
                <Grid size={6}>
                    <Grid container spacing={4}>
                        <Grid size={12}>
                            <SubscriptionInformation cameraAdmin={data.cameraAdmin} />
                        </Grid>
                        <Grid size={12}>
                            <EditForm cameraAdmin={data.cameraAdmin} />
                        </Grid>
                        <Grid size={12}>
                            <ActivityLogTable cameraAdmin={data.cameraAdmin} />
                        </Grid>
                        <Grid size={12}>
                            <PurchaseInformation cameraAdmin={data.cameraAdmin} />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid size={6}>
                    <Grid container spacing={4}>
                        <ActivityInformation cameraAdmin={data.cameraAdmin} refetchCamera={refetch} />
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};
